(() => {
  const elementName = 'fendo-variant-pagination-v47';

  if (window.customElements.get(elementName)) return;

  const resultCache = window.__fendoVariantPaginationCacheV47 || new Map();
  window.__fendoVariantPaginationCacheV47 = resultCache;

  const pendingFacetUpdates = new WeakMap();

  const clearPendingFacetUpdate = (section) => {
    const pending = section ? pendingFacetUpdates.get(section) : null;
    if (!pending) return;

    window.clearTimeout(pending.timer);
    pendingFacetUpdates.delete(section);
  };

  const normalize = (value) =>
    String(value ?? '')
      .replace(/\u00a0/g, ' ')
      .normalize('NFKC')
      .toLocaleLowerCase()
      .replace(/ß/g, 'ss')
      .replace(/\s+/g, ' ')
      .trim();

  const stripCount = (value) => String(value ?? '').replace(/\s*\(\d+\)\s*$/, '').trim();

  const getValueAliases = (value) => {
    const source = stripCount(value);
    const aliases = new Set();

    const addAlias = (candidate) => {
      const normalized = normalize(candidate);
      if (!normalized) return;

      aliases.add(normalized);

      const compact = normalized.replace(/\s+/g, '');
      const equivalents = {
        xxl: '2xl',
        '2xl': 'xxl',
        xxxl: '3xl',
        '3xl': 'xxxl',
        xxxxl: '4xl',
        '4xl': 'xxxxl'
      };

      if (equivalents[compact]) aliases.add(equivalents[compact]);
    };

    addAlias(source);

    const openIndex = source.lastIndexOf('(');
    const closeIndex = source.lastIndexOf(')');

    if (openIndex >= 0 && closeIndex > openIndex) {
      addAlias(source.slice(openIndex + 1, closeIndex));
    }

    return aliases;
  };

  const aliasSetsIntersect = (first, second) => {
    for (const value of first) {
      if (second.has(value)) return true;
    }

    return false;
  };

  const isSupportedVariantParam = (param) => {
    const rawParam = String(param || '').toLowerCase();

    // Shopify taxonomy colour filters (for example color-pattern) are intentionally
    // left to Shopify. We only post-process the taxonomy size filter and real
    // variant-option filters, whose values can be matched against variant.options.
    return rawParam.includes('filter.v.t.shopify.size')
      || rawParam.includes('filter.v.option.');
  };

  const isSupportedVariantFilter = (filter) => isSupportedVariantParam(filter?.param);

  const urlHasSupportedVariantFilter = (url) => {
    for (const [param, value] of url.searchParams.entries()) {
      if (isSupportedVariantParam(param) && String(value).trim() !== '') return true;
    }

    return false;
  };

  const setSectionLoadingState = (section, isLoading) => {
    const collectionMain = section?.querySelector('[data-fendo-collection-main]');
    if (!collectionMain) return;

    const loader = collectionMain.querySelector('[data-fendo-variant-loader]');
    const productList = collectionMain.querySelector('product-list');

    if (isLoading) {
      collectionMain.setAttribute('data-fendo-loading', 'true');
      collectionMain.setAttribute('aria-busy', 'true');
      loader?.removeAttribute('hidden');
      productList?.setAttribute('aria-hidden', 'true');
    } else {
      collectionMain.removeAttribute('data-fendo-loading');
      collectionMain.removeAttribute('aria-busy');
      loader?.setAttribute('hidden', '');
      productList?.removeAttribute('aria-hidden');
    }
  };

  class FendoVariantPagination extends HTMLElement {
    connectedCallback() {
      this.section = this.closest('.shopify-section');
      clearPendingFacetUpdate(this.section);
      this.productList = this.section?.querySelector(`#${CSS.escape(this.dataset.productListId || '')}`);
      this.paginationMount = this.section?.querySelector('[data-fendo-pagination-mount]');
      this.emptyTemplate = this.section?.querySelector('template[data-fendo-empty-template]');
      this.collectionMain = this.section?.querySelector('[data-fendo-collection-main]');
      this.loader = this.section?.querySelector('[data-fendo-variant-loader]');
      this.abortController = new AbortController();
      this.activeFilters = this.readActiveFilters();

      if (
        this.dataset.active !== 'true' ||
        !this.section ||
        !this.productList ||
        !this.paginationMount ||
        this.activeFilters.length === 0
      ) {
        this.stopLoading();
        return;
      }

      this.startLoading();
      this.paginationMount.addEventListener('click', this.onPaginationClick);
      window.addEventListener('popstate', this.onPopState);
      this.loadProducts();
    }

    disconnectedCallback() {
      this.abortController?.abort();
      this.paginationMount?.removeEventListener('click', this.onPaginationClick);
      window.removeEventListener('popstate', this.onPopState);
      this.stopLoading();
    }

    cancelForFacetUpdate() {
      // The current section is about to be replaced by Prestige. Abort its background
      // page requests, but keep the immediate loader visible until replacement.
      this.cancelledForFacetUpdate = true;
      this.abortController?.abort();
    }

    startLoading() {
      if (this.isLoading) return;

      this.isLoading = true;
      this.collectionMain?.setAttribute('data-fendo-loading', 'true');
      this.collectionMain?.setAttribute('aria-busy', 'true');
      this.loader?.removeAttribute('hidden');
      this.productList?.setAttribute('aria-hidden', 'true');
      document.documentElement.dispatchEvent(new CustomEvent('theme:loading:start', { bubbles: true }));
    }

    stopLoading() {
      const wasLoading = this.isLoading || this.collectionMain?.getAttribute('data-fendo-loading') === 'true';

      this.isLoading = false;
      this.collectionMain?.removeAttribute('data-fendo-loading');
      this.collectionMain?.removeAttribute('aria-busy');
      this.loader?.setAttribute('hidden', '');
      this.productList?.removeAttribute('aria-hidden');

      if (wasLoading) {
        document.documentElement.dispatchEvent(new CustomEvent('theme:loading:end', { bubbles: true }));
      }
    }

    readActiveFilters() {
      const script = this.section?.querySelector('script[data-fendo-active-variant-filters]');

      if (script) {
        try {
          const parsed = JSON.parse(script.textContent || '[]');
          const filters = parsed
            .map((filter) => ({
              param: String(filter.param || ''),
              label: stripCount(filter.label),
              values: [...new Set((filter.values || []).map(stripCount).filter(Boolean))]
            }))
            .filter((filter) => isSupportedVariantFilter(filter) && filter.values.length > 0);

          if (filters.length > 0) return filters;
        } catch (error) {
          console.warn('[Fendo] Could not parse active variant filters:', error);
        }
      }

      return [];
    }

    onPaginationClick = (event) => {
      const link = event.target.closest('[data-fendo-page]');

      if (
        !link ||
        event.defaultPrevented ||
        event.button !== 0 ||
        event.metaKey ||
        event.ctrlKey ||
        event.shiftKey ||
        event.altKey
      ) {
        return;
      }

      event.preventDefault();

      const page = Number.parseInt(link.dataset.fendoPage, 10);
      if (!Number.isFinite(page) || page < 1) return;

      history.pushState({ fendoVariantPage: page }, '', link.href);
      this.renderPage(page);
      const behavior = window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
      this.section.querySelector('.collection')?.scrollIntoView({ block: 'start', behavior });
    };

    onPopState = () => {
      if (!this.products) return;
      this.renderPage(this.getRequestedPage());
    };

    async loadProducts() {
      this.startLoading();
      this.productList.setAttribute('aria-busy', 'true');

      try {
        const cacheKey = this.getCacheKey();
        let result = resultCache.get(cacheKey);

        if (!result) {
          result = await this.collectAllPages();
          resultCache.set(cacheKey, result);
        }

        this.products = result.products;
        this.promos = result.promos;
        this.renderPage(this.getRequestedPage());
      } catch (error) {
        if (error.name !== 'AbortError') {
          console.error('[Fendo] Variant pagination failed; keeping Shopify native results.', error);
        }
      } finally {
        this.productList.removeAttribute('aria-busy');

        if (!this.cancelledForFacetUpdate) {
          this.stopLoading();
        }
      }
    }

    getCacheKey() {
      const url = new URL(window.location.href);
      url.searchParams.delete('page');
      url.searchParams.delete('section_id');
      url.hash = '';

      return `v47|${this.dataset.sectionId}|${url.pathname}?${url.searchParams.toString()}|${JSON.stringify(this.activeFilters)}`;
    }

    getRequestedPage() {
      const page = Number.parseInt(new URL(window.location.href).searchParams.get('page') || '1', 10);
      return Number.isFinite(page) && page > 0 ? page : 1;
    }

    async collectAllPages() {
      const nativePages = Math.max(1, Number.parseInt(this.dataset.nativePages || '1', 10));
      const currentNativePage = Math.max(1, Number.parseInt(this.dataset.nativePage || '1', 10));
      const pages = new Map();

      pages.set(currentNativePage, this.extractPage(this.section));

      const pagesToFetch = [];
      for (let page = 1; page <= nativePages; page += 1) {
        if (page !== currentNativePage) pagesToFetch.push(page);
      }

      let nextIndex = 0;
      const workerCount = Math.min(2, pagesToFetch.length);

      const worker = async () => {
        while (nextIndex < pagesToFetch.length) {
          const page = pagesToFetch[nextIndex];
          nextIndex += 1;
          pages.set(page, await this.fetchPage(page));
        }
      };

      await Promise.all(Array.from({ length: workerCount }, worker));

      const products = [];
      const promos = [];
      const seenProducts = new Set();

      for (let page = 1; page <= nativePages; page += 1) {
        const pageResult = pages.get(page);
        if (!pageResult) continue;

        for (const product of pageResult.products) {
          if (seenProducts.has(product.key)) continue;
          seenProducts.add(product.key);

          if (!product.data) {
            throw new Error(`Embedded variant data is missing for product: ${product.key}`);
          }

          if (this.hasMatchingAvailableVariant(product.data)) {
            products.push(product);
          }
        }

        if (page === 1) promos.push(...pageResult.promos);
      }

      return { products, promos };
    }

    async fetchPage(page) {
      const url = new URL(window.location.href);

      if (page === 1) url.searchParams.delete('page');
      else url.searchParams.set('page', String(page));

      url.searchParams.set('section_id', this.dataset.sectionId);

      let lastError;
      const retryDelays = [0, 500, 1500];

      for (const delay of retryDelays) {
        if (delay > 0) await this.wait(delay);

        try {
          const response = await fetch(url.toString(), {
            signal: this.abortController.signal,
            credentials: 'same-origin',
            headers: { 'X-Requested-With': 'XMLHttpRequest' }
          });

          if (!response.ok) throw new Error(`HTTP ${response.status}`);

          const html = await response.text();
          const parsedDocument = new DOMParser().parseFromString(html, 'text/html');
          const section = parsedDocument.querySelector(`#shopify-section-${CSS.escape(this.dataset.sectionId)}`)
            || parsedDocument.querySelector('.shopify-section');

          if (!section) throw new Error('Collection section was not found in the response.');
          return this.extractPage(section);
        } catch (error) {
          lastError = error;
          if (error.name === 'AbortError') throw error;
        }
      }

      throw lastError || new Error('Could not load collection page.');
    }

    extractPage(section) {
      const list = section.querySelector(`#${CSS.escape(this.dataset.productListId)}`);
      if (!list) return { products: [], promos: [] };

      const dataByKey = new Map();
      const dataScript = section.querySelector('script[data-fendo-page-products]');

      if (dataScript) {
        try {
          const records = JSON.parse(dataScript.textContent || '[]');
          for (const record of records) {
            const key = String(record?.key || '').trim();
            if (key) dataByKey.set(key, record);
          }
        } catch (error) {
          console.warn('[Fendo] Could not parse embedded product variant data:', error);
        }
      }

      const products = [];
      const promos = [];
      let productPosition = 0;

      for (const element of Array.from(list.children)) {
        if (element.matches('product-card')) {
          productPosition += 1;

          const productLink = element.querySelector('a[href*="/products/"]');
          if (!productLink) continue;

          const productUrl = new URL(productLink.href, window.location.origin);
          productUrl.search = '';
          productUrl.hash = '';

          const handle = element.getAttribute('handle') || productUrl.pathname.split('/').filter(Boolean).pop();
          const key = handle || productUrl.pathname;

          products.push({
            key,
            html: element.outerHTML,
            data: dataByKey.get(handle) || null
          });
        } else if (element.matches('.product-list__promo')) {
          promos.push({
            position: productPosition + 1,
            html: element.outerHTML
          });
        }
      }

      return { products, promos };
    }

    wait(milliseconds) {
      return new Promise((resolve, reject) => {
        if (this.abortController.signal.aborted) {
          reject(new DOMException('Aborted', 'AbortError'));
          return;
        }

        const timer = window.setTimeout(() => {
          this.abortController.signal.removeEventListener('abort', onAbort);
          resolve();
        }, milliseconds);

        const onAbort = () => {
          window.clearTimeout(timer);
          reject(new DOMException('Aborted', 'AbortError'));
        };

        this.abortController.signal.addEventListener('abort', onAbort, { once: true });
      });
    }

    hasMatchingAvailableVariant(product) {
      const variants = Array.isArray(product.variants) ? product.variants : [];
      if (variants.length === 0) return false;

      const filterIndexes = this.activeFilters.map((filter) => ({
        filter,
        index: this.resolveOptionIndex(product, filter)
      }));

      if (filterIndexes.some(({ index }) => index < 0)) return false;

      return variants.some((variant) => {
        if (variant.available !== true) return false;

        return filterIndexes.every(({ filter, index }) => {
          const optionAliases = getValueAliases(variant.options?.[index]);

          return filter.values.some((value) =>
            aliasSetsIntersect(optionAliases, getValueAliases(value))
          );
        });
      });
    }

    resolveOptionIndex(product, filter) {
      const optionNames = (product.options || []).map((option) =>
        typeof option === 'string' ? option : option?.name
      );
      const normalizedNames = optionNames.map(normalize);
      const label = normalize(filter.label);
      const paramTail = normalize(filter.param.split('.').pop());

      let index = normalizedNames.findIndex((name) => name === label || name === paramTail);
      if (index >= 0) return index;

      const sizeAliases = new Set(['size', 'grosse', 'groesse', 'taille', 'misura', 'talla']);

      if (sizeAliases.has(label) || sizeAliases.has(paramTail)) {
        index = normalizedNames.findIndex((name) => sizeAliases.has(name));
        if (index >= 0) return index;
      }


      const selectedValues = filter.values.map(getValueAliases);
      const matchingIndexes = [];
      const optionCount = product.variants?.[0]?.options?.length || optionNames.length;

      for (let optionIndex = 0; optionIndex < optionCount; optionIndex += 1) {
        const availableValues = (product.variants || []).map((variant) =>
          getValueAliases(variant.options?.[optionIndex])
        );

        const everySelectedValueExists = selectedValues.every((selectedAliases) =>
          availableValues.some((availableAliases) =>
            aliasSetsIntersect(selectedAliases, availableAliases)
          )
        );

        if (everySelectedValueExists) matchingIndexes.push(optionIndex);
      }

      return matchingIndexes.length === 1 ? matchingIndexes[0] : -1;
    }

    renderPage(requestedPage) {
      const perPage = Math.max(1, Number.parseInt(this.dataset.perPage || '48', 10));
      const totalProducts = this.products.length;
      const totalPages = Math.max(1, Math.ceil(totalProducts / perPage));
      const currentPage = Math.min(Math.max(1, requestedPage), totalPages);

      if (currentPage !== requestedPage) {
        const correctedUrl = new URL(window.location.href);
        if (currentPage === 1) correctedUrl.searchParams.delete('page');
        else correctedUrl.searchParams.set('page', String(currentPage));
        history.replaceState({}, '', correctedUrl);
      }

      const start = (currentPage - 1) * perPage;
      const visibleProducts = this.products.slice(start, start + perPage);
      const fragment = document.createDocumentFragment();
      const promosByPosition = new Map();

      for (const promo of this.promos || []) {
        if (!promosByPosition.has(promo.position)) promosByPosition.set(promo.position, []);
        promosByPosition.get(promo.position).push(promo);
      }

      for (let index = 0; index < visibleProducts.length; index += 1) {
        const position = index + 1;

        for (const promo of promosByPosition.get(position) || []) {
          const promoNode = this.createNode(promo.html);
          if (promoNode) fragment.append(promoNode);
        }

        const productNode = this.createNode(visibleProducts[index].html);
        if (productNode) {
          this.prepareProductNode(productNode, index);
          fragment.append(productNode);
        }
      }

      this.section.querySelector('[data-fendo-empty-rendered]')?.remove();
      this.productList.hidden = totalProducts === 0;
      this.productList.replaceChildren(fragment);

      if (totalProducts === 0 && this.emptyTemplate) {
        this.productList.before(this.emptyTemplate.content.cloneNode(true));
      }

      this.updateCount(totalProducts);
      this.renderPagination(currentPage, totalPages);
    }

    createNode(html) {
      const template = document.createElement('template');
      template.innerHTML = html.trim();
      const node = template.content.firstElementChild;
      if (!node) return null;

      node.querySelectorAll('[reveal-on-scroll]').forEach((element) => {
        element.removeAttribute('reveal-on-scroll');
        element.style.removeProperty('opacity');
        element.style.removeProperty('visibility');
        element.style.removeProperty('transform');
      });

      if (node.hasAttribute('reveal-on-scroll')) {
        node.removeAttribute('reveal-on-scroll');
        node.style.removeProperty('opacity');
        node.style.removeProperty('visibility');
        node.style.removeProperty('transform');
      }

      return node;
    }

    prepareProductNode(node, visibleIndex) {
      const images = Array.from(node.querySelectorAll('img'));

      for (const image of images) {
        image.setAttribute('loading', 'lazy');
        image.setAttribute('fetchpriority', 'low');
      }

      const primaryImage = node.querySelector('.product-card__image--primary');
      if (primaryImage && visibleIndex < 3) {
        primaryImage.setAttribute('loading', 'eager');
        primaryImage.setAttribute('fetchpriority', visibleIndex === 0 ? 'high' : 'auto');
      }
    }

    updateCount(total) {
      this.section.querySelectorAll('[data-fendo-products-count]').forEach((element) => {
        const template = total === 1
          ? element.dataset.fendoCountOne
          : element.dataset.fendoCountMany;

        if (template) element.textContent = template.replace('__COUNT__', String(total));
      });
    }

    renderPagination(currentPage, totalPages) {
      this.paginationMount.replaceChildren();
      if (totalPages <= 1) return;

      const nav = document.createElement('nav');
      nav.className = 'pagination';
      nav.setAttribute('role', 'navigation');
      nav.setAttribute('aria-label', this.dataset.paginationLabel || 'Pagination');

      if (currentPage > 1) {
        nav.append(this.createPageLink(currentPage - 1, 'prev', this.createChevron('left')));
      }

      for (const item of this.getPaginationItems(currentPage, totalPages)) {
        if (item === 'ellipsis') {
          const ellipsis = document.createElement('span');
          ellipsis.className = 'pagination__link pagination__link--disabled h6';
          ellipsis.textContent = '…';
          nav.append(ellipsis);
          continue;
        }

        if (item === currentPage) {
          const current = document.createElement('span');
          current.className = 'pagination__link pagination__link--disabled h6';
          current.setAttribute('aria-current', 'page');
          current.textContent = String(item);
          nav.append(current);
        } else {
          nav.append(this.createPageLink(item, '', document.createTextNode(String(item))));
        }
      }

      if (currentPage < totalPages) {
        nav.append(this.createPageLink(currentPage + 1, 'next', this.createChevron('right')));
      }

      this.paginationMount.append(nav);
    }

    createPageLink(page, rel, content) {
      const link = document.createElement('a');
      const url = new URL(window.location.href);

      if (page === 1) url.searchParams.delete('page');
      else url.searchParams.set('page', String(page));

      link.className = 'pagination__link h6';
      link.href = url.toString();
      link.dataset.fendoPage = String(page);
      link.setAttribute('aria-label', this.getPageLabel(page));
      if (rel) link.rel = rel;
      link.append(content);

      return link;
    }

    getPageLabel(page) {
      const template = this.dataset.goToPageLabel || 'Go to page __PAGE__';
      return template.replace('__PAGE__', String(page));
    }

    createChevron(direction) {
      const namespace = 'http://www.w3.org/2000/svg';
      const svg = document.createElementNS(namespace, 'svg');
      const path = document.createElementNS(namespace, 'path');

      svg.setAttribute('aria-hidden', 'true');
      svg.setAttribute('focusable', 'false');
      svg.setAttribute('fill', 'none');
      svg.setAttribute('width', '11');
      svg.setAttribute('class', `icon icon-chevron-${direction} icon--direction-aware`);
      svg.setAttribute('viewBox', '0 0 10 10');

      path.setAttribute('d', direction === 'left' ? 'M7 1 3 5l4 4' : 'm3 9 4-4-4-4');
      path.setAttribute('stroke', 'currentColor');
      path.setAttribute('stroke-linecap', 'square');
      svg.append(path);

      return svg;
    }

    getPaginationItems(currentPage, totalPages) {
      if (totalPages <= 7) {
        return Array.from({ length: totalPages }, (_, index) => index + 1);
      }

      const pages = new Set([1, totalPages, currentPage - 1, currentPage, currentPage + 1]);
      const sorted = Array.from(pages)
        .filter((page) => page >= 1 && page <= totalPages)
        .sort((a, b) => a - b);
      const result = [];

      sorted.forEach((page, index) => {
        if (index > 0 && page - sorted[index - 1] > 1) result.push('ellipsis');
        result.push(page);
      });

      return result;
    }
  }

  // Prestige starts its section request on `facet:update`. The old implementation
  // could only display the large collection loader after that first response arrived,
  // which made the filter feel unresponsive. Capture the event and show the loader
  // immediately, while also cancelling stale background pagination requests.
  document.addEventListener('facet:update', (event) => {
    let url;

    try {
      url = event.detail?.url instanceof URL
        ? event.detail.url
        : new URL(String(event.detail?.url || ''), window.location.href);
    } catch (_) {
      return;
    }

    const sectionId = url.searchParams.get('section_id');
    if (!sectionId) return;

    const section = document.getElementById(`shopify-section-${sectionId}`);
    if (!section) return;

    section.querySelectorAll(elementName).forEach((element) => {
      element.cancelForFacetUpdate?.();
    });

    clearPendingFacetUpdate(section);

    const shouldShowLoader = urlHasSupportedVariantFilter(url);
    setSectionLoadingState(section, shouldShowLoader);

    if (shouldShowLoader) {
      const collectionMain = section.querySelector('[data-fendo-collection-main]');
      const token = Symbol('fendo-facet-update');
      const timer = window.setTimeout(() => {
        const pending = pendingFacetUpdates.get(section);

        if (pending?.token !== token) return;

        pendingFacetUpdates.delete(section);

        // If Prestige successfully replaced the section, the old main node is no
        // longer connected. If it is still connected, the native request failed or
        // was interrupted, so reveal the last valid Shopify results instead of
        // leaving the overlay stuck forever.
        if (collectionMain?.isConnected) {
          setSectionLoadingState(section, false);
        }
      }, 20000);

      pendingFacetUpdates.set(section, { token, timer });
    }
  }, true);

  window.__fendoVariantPaginationV47 = true;
  window.customElements.define(elementName, FendoVariantPagination);
})();
